import { createSelector } from 'reselect';
import type { State } from '~/store';
import { selectWorkflowDataSnapshotById } from './history';
import { selectPublicVersion, selectWorkflowData, selectWorkflowProps } from './general';
import { getIsInSnippetPastePendingMode, getIsInSnippetPreviewMode, getIsInSnippetReviewMode } from 'flow/service/snippets';
import generateMergeDiff from 'flow/service/changelog/formatters/mergeDropdownFormatter/mergeDiff';
import type { RuleBuilderRule } from 'flow/service/rules';
import type { Action, PublicId } from 'flow/store/engine/types';

export const selectSnippetMode = createSelector(
  (state: State) => state.workflowEditor.ui.snippet.mode,
  mode => mode,
);

export function selectIsPreviewSnippetMode(state: State) {
  const snippetMode = selectSnippetMode(state);
  return snippetMode ? getIsInSnippetPreviewMode(snippetMode) : false;
}

export function selectIsReviewSnippetMode(state: State) {
  const snippetMode = selectSnippetMode(state);
  return snippetMode ? getIsInSnippetReviewMode(snippetMode) : false;
}

export function selectIsPastePendingSnippetMode(state: State) {
  const snippetMode = selectSnippetMode(state);
  return snippetMode ? getIsInSnippetPastePendingMode(snippetMode) : false;
}

export const selectPreSnippetWorkflowDataSnapshotId = createSelector(
  (state: State) => state.workflowEditor.ui.snippet.preSnippetWorkflowDataSnapshotId,
  preInsertionSnapshotId => preInsertionSnapshotId,
);

export function selectPreSnippetWorkflowDataSnapshot(state: State) {
  const snapshotId = selectPreSnippetWorkflowDataSnapshotId(state);
  if (!snapshotId) {
    return null;
  }
  return selectWorkflowDataSnapshotById(snapshotId)(state);
}

export function selectPreSnippetWorkflowPropsSnapshot(state: State) {
  return state.workflowEditor.ui.snippet.preSnippetWorkflowPropsSnapshot;
}

export const selectSnippetMetadata = createSelector(
  (state: State) => state.workflowEditor.snippet,
  snippet => snippet.metadata,
);

export const selectIsSnippetVersionPublished = createSelector(
  [selectPublicVersion, selectSnippetMetadata],
  (publicVersion, metadata) => {
    if (!metadata) {
      return false;
    }

    if (publicVersion === null) {
      return false;
    }

    return metadata.published_version === publicVersion;
  },
);

export const selectMergeDiff = createSelector(
  [
    selectPreSnippetWorkflowDataSnapshot,
    selectWorkflowData,
    selectWorkflowProps,
  ],
  (preInsertSnapshot, workflowData, workflowProps) => {
    if (!preInsertSnapshot) return null;
    return generateMergeDiff({
      A: {
        props: workflowProps,
        version: preInsertSnapshot.data,
      },
      B: {
        props: workflowProps,
        version: workflowData,
      },
    });
  },
);

export function selectIsMergeModified(
  publicId: PublicId,
) {
  return function selector(state: State) {
    const diff = selectMergeDiff(state);

    if (!diff) return false;

    return diff.modified.has(publicId);
  };
}

export function selectIsMergeAdded(
  publicId: PublicId,
) {
  return function selector(state: State) {
    const diff = selectMergeDiff(state);

    if (!diff) return false;

    return diff.added.has(publicId);
  };
}

export function selectIsRuleDiffed(
  ruleId: RuleBuilderRule['id'],
  sourceActionId: Action['publicId'],
) {
  return function selector(state: State) {
    return selectIsMergeModified(sourceActionId)(state) && selectIsMergeAdded(ruleId)(state);
  };
}

